import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSuiClient } from "@mysten/dapp-kit";
import { Flame, Sparkles, Crown, Rocket, ArrowRightLeft, Activity, LayoutGrid, List, Search, Plus, ArrowRight, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { useDeployment } from "@/context/DeploymentContext";
import { useWallet } from "@/context/WalletContext";
import { getCurveState } from "@/lib/sui";
import { onchainMetrics, fmtNum, fmtUsd, fmtSui } from "@/lib/format";
import CoinCard from "@/components/CoinCard";
import CoinRow from "@/components/CoinRow";
import CoinCardSkeleton from "@/components/CoinCardSkeleton";
import CoinLogo from "@/components/CoinLogo";
import BondingCurveBar from "@/components/BondingCurveBar";
import TradeTicker from "@/components/TradeTicker";
import { useLive } from "@/context/LiveContext";
import { liveDebounceMs, isHidden, isTouch } from "@/lib/perf";

const SORTS = [
  { key: "trending", label: "Trending", icon: Flame },
  { key: "new", label: "New", icon: Sparkles },
  { key: "bonding", label: "Bonding", icon: Activity },
  { key: "graduated", label: "Graduated", icon: ArrowRightLeft },
];

// Module-level cache so returning to the board paints instantly from the last fetch.
let _cachedCoins = [];
let _cachedKing = null;
let _cachedStats = null;
let _cachedSuiUsd = null;

export default function Board({ onCreate }) {
  const [coins, setCoins] = useState(_cachedCoins);
  const [king, setKing] = useState(_cachedKing);
  const [sort, setSort] = useState("trending");
  const [search, setSearch] = useState("");
  const [view, setView] = useState(() => localStorage.getItem("suipump_view") || "grid");
  const [loading, setLoading] = useState(_cachedCoins.length === 0);
  const [suiUsd, setSuiUsd] = useState(_cachedSuiUsd);
  const [stats, setStats] = useState(_cachedStats);
  const [devLocks, setDevLocks] = useState({});
  const navigate = useNavigate();
  const { deployed } = useDeployment();
  const { address } = useWallet();
  const client = useSuiClient();
  const { subscribe } = useLive();

  const changeView = (v) => { setView(v); localStorage.setItem("suipump_view", v); };

  const loadOnchain = useCallback(async () => {
    const { data } = await api.get("/coins/onchain");
    _cachedCoins = data;
    let list = data;
    if (search) list = list.filter((c) => (c.name + c.symbol).toLowerCase().includes(search.toLowerCase()));
    const active = list.filter((c) => !c.migrated);
    if (sort === "new") list = [...active].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    else if (sort === "graduated") list = list.filter((c) => c.migrated).sort((a, b) => b.market_cap_sui - a.market_cap_sui);
    else if (sort === "bonding") list = [...active].sort((a, b) => b.real_sui_collected - a.real_sui_collected);
    else list = [...active].sort((a, b) => (b.market_cap_sui || 0) - (a.market_cap_sui || 0) || (b.trend_score || 0) - (a.trend_score || 0));
    setCoins(list);
    const k = [...active].sort((a, b) => (b.market_cap_sui || 0) - (a.market_cap_sui || 0) || (b.trend_score || 0) - (a.trend_score || 0))[0] || null;
    _cachedKing = k;
    setKing(k);
  }, [search, sort]);

  const load = useCallback(async () => {
    try { await loadOnchain(); }
    catch (e) {} finally { setLoading(false); }
  }, [loadOnchain]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { if (!isHidden()) load(); }, 30000);
    return () => clearInterval(t);
  }, [load]);

  const liveTimer = useRef(null);
  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type !== "trade" || isHidden()) return;
      if (liveTimer.current) clearTimeout(liveTimer.current);
      liveTimer.current = setTimeout(load, liveDebounceMs);
    });
  }, [subscribe, load]);
  useEffect(() => { api.get("/sui-price").then(({ data }) => { if (data?.usd) { _cachedSuiUsd = data.usd; setSuiUsd(data.usd); } }).catch(() => {}); }, []);
  useEffect(() => {
    const f = () => api.get("/stats/onchain").then(({ data }) => { _cachedStats = data; setStats(data); }).catch(() => {});
    f(); const t = setInterval(f, 30000); return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const f = () => api.get("/vesting/all").then(({ data }) => {
      const map = {};
      (data || []).forEach((l) => {
        if (!l.is_dev || !l.curve_id) return;
        const e = map[l.curve_id] || { tokens: 0, days: 0 };
        e.tokens += l.amount || 0;
        e.days = Math.max(e.days, l.days || 0);
        map[l.curve_id] = e;
      });
      setDevLocks(map);
    }).catch(() => {});
    f(); const t = setInterval(f, 60000); return () => clearInterval(t);
  }, []);

  const statPills = [
    { label: "Tokens", value: stats ? Number(stats.tokens).toLocaleString() : "—", icon: Rocket, color: "#1EC62E", testid: "stat-tokens" },
    { label: "Trades", value: stats ? Number(stats.trades).toLocaleString() : "—", icon: ArrowRightLeft, color: "#00E5FF", testid: "stat-trades" },
    { label: "Volume", value: stats ? `${fmtNum(stats.volume_sui)} SUI` : "—", icon: Activity, color: "#22C55E", testid: "stat-volume" },
    { label: "SUI", value: suiUsd ? `$${Number(suiUsd).toFixed(4)}` : "—", icon: TrendingUp, color: "#1EC62E", testid: "stat-sui-price" },
  ];

  return (
    <>
    <TradeTicker />
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">

      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden rounded-3xl border border-white/10 mb-8 px-6 py-9 sm:px-10 sm:py-12" data-testid="hero">
        <div className="hero-mesh" aria-hidden="true" />
        <div className="relative max-w-3xl">
          <span className="eyebrow">Sui Mainnet · Live</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[1.04]">
            Launch &amp; trade <span className="gradient-text">Sui</span> memecoins on a fair curve
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-4 max-w-xl leading-relaxed">
            Every coin starts fair on a bonding curve. Hit 7,500 SUI and it auto-migrates to Cetus with permanently locked liquidity. 1% fee, enforced on-chain.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <button onClick={() => onCreate && onCreate()} data-testid="hero-create-button" className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm">
              <Plus className="w-4 h-4" /> Launch a Coin
            </button>
            <a href="#discover" data-testid="hero-explore-button" className="btn-ghost inline-flex items-center gap-2 px-6 py-3 text-sm">
              Explore board <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-9" data-testid="platform-stats">
            {statPills.map((s) => (
              <div key={s.label} data-testid={s.testid} className="stat-pill">
                <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0" style={{ background: `${s.color}1f` }}>
                  <s.icon className="w-4 h-4" style={{ color: s.color }} />
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{s.label}</div>
                  <div className="font-display font-extrabold text-base sm:text-lg leading-tight truncate">{s.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- KING OF THE HILL SPOTLIGHT ---------- */}
      {king && (
        <div onClick={() => navigate(`/coin/${king.id || king.curve_id}`)} data-testid="king-of-hill-banner" className="koth-banner group relative overflow-hidden rounded-3xl p-5 sm:p-6 mb-8 cursor-pointer border border-[#FFB800]/40 sm:transition-transform sm:hover:-translate-y-0.5">
          {!isTouch && <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle,#FFB800,transparent 70%)" }} />}
          {!isTouch && <div className="absolute inset-y-0 -left-1/3 w-1/3 skew-x-12 bg-white/5 group-hover:translate-x-[420%] transition-transform duration-1000" />}

          <div className="relative flex items-center justify-between gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40">
              <Crown className="w-3.5 h-3.5 text-[#FFB800]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FFB800]">King of the Hill</span>
            </span>
            <span className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-[#FFB800] opacity-0 group-hover:opacity-100 transition-opacity">Open <ArrowRight className="w-3.5 h-3.5" /></span>
          </div>

          <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="relative shrink-0 rounded-2xl p-[2px]" style={{ background: "linear-gradient(135deg,#FFB800,#FF6B00)" }}>
                <CoinLogo coin={king} size={64} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-extrabold text-lg sm:text-2xl truncate max-w-full">{king.name}</span>
                  <span className="font-mono text-sm text-[#1EC62E]">${king.symbol}</span>
                  {king.buys_1h > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF6B00]/20 text-[#FF6B00] text-[10px] font-bold uppercase"><Flame className="w-3 h-3" /> Hot</span>}
                </div>
                <div className="mt-3 w-full max-w-md"><BondingCurveBar progress={king.progress} migrated={king.migrated} current={king.real_sui_collected} remaining={king.sui_remaining} gradSui={king.graduation_sui || 7500} showLabel /></div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center sm:gap-8 sm:pl-6 sm:border-l border-white/10 shrink-0">
              <div className="rounded-xl bg-white/[0.03] px-3 py-2 sm:bg-transparent sm:p-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Market cap</div>
                <div className="font-display font-extrabold text-base sm:text-xl">{suiUsd ? fmtUsd((king.market_cap_sui || 0) * suiUsd) : "—"}</div>
              </div>
              <div className="rounded-xl bg-white/[0.03] px-3 py-2 sm:bg-transparent sm:p-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">24h vol</div>
                <div className="font-mono font-bold text-base sm:text-xl text-[#1EC62E]">{fmtSui(king.vol_24h_sui || 0, 2)} SUI</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- DISCOVERY TOOLBAR ---------- */}
      <div id="discover" className="scroll-mt-24 mb-5">
        <div className="relative mb-4" data-testid="board-search">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="coin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search coins, symbols..."
            className="w-full bg-white/5 border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#1EC62E]/50 transition-colors"
          />
        </div>

        <div className="flex items-center gap-3">
          <div className="seg overflow-x-auto flex-1" style={{ scrollbarWidth: "none" }}>
            {SORTS.map((s) => {
              const Icon = s.icon;
              return (
                <button key={s.key} data-testid={`sort-${s.key}`} data-active={sort === s.key} onClick={() => setSort(s.key)} className="seg-tab">
                  <Icon className="w-4 h-4" /> {s.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-1 p-1 rounded-full border border-white/10 bg-white/[0.03] shrink-0" data-testid="view-toggle">
            <button data-testid="view-grid" onClick={() => changeView("grid")} title="Grid view" aria-label="Grid view"
              className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${view === "grid" ? "bg-[#1EC62E]/15 text-[#1EC62E]" : "text-muted-foreground hover:text-white"}`}>
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button data-testid="view-list" onClick={() => changeView("list")} title="List view" aria-label="List view"
              className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${view === "list" ? "bg-[#1EC62E]/15 text-[#1EC62E]" : "text-muted-foreground hover:text-white"}`}>
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ---------- GRID / LIST ---------- */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="board-skeletons">
          {Array.from({ length: 8 }).map((_, i) => <CoinCardSkeleton key={i} />)}
        </div>
      ) : coins.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground"><Rocket className="w-10 h-10 mx-auto mb-3 opacity-40" />{deployed ? "No coins launched yet. Be the first — hit Create Coin!" : "No coins found."}</div>
      ) : view === "list" ? (
        <div className="flex flex-col gap-2" data-testid="board-list">
          {coins.map((c, i) => (
            <div key={c.id || c.curve_id} className="animate-fade-up" style={{ animationDelay: `${Math.min(i, 10) * 0.03}s` }}>
              <CoinRow coin={c} suiUsd={suiUsd} />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="board-grid">
          {coins.map((c, i) => (
            <div key={c.id || c.curve_id} className="animate-fade-up" style={{ animationDelay: `${Math.min(i, 12) * 0.03}s` }}>
              <CoinCard coin={c} suiUsd={suiUsd} devLock={devLocks[c.curve_id]} />
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
}
